import { ExploreAggInteractionRow } from "@/v2/entities/explore/Explore.model";
import { FieldFilter } from "@/v2/components/filter-menu/FieldFilter.model";
import {
  ExploreColumnType,
  ExploreGroupByColumnType,
  isExploreGroupByColumnType,
} from "@/v2/entities/explore/columns/ExploreColum.model";
import { ExploreState } from "./ExploreState.model";
import { useCallback } from "react";

const hierarchy: Record<
  ExploreGroupByColumnType,
  ExploreGroupByColumnType | undefined
> = {
  status: undefined,
  channel: undefined,
  team: "agentAssigned",
  agentAssigned: undefined,
  topic: undefined,
  subTopic: undefined,
  category: undefined,
  day: undefined,
  month: undefined,
  avgQaScore: undefined,
  avgQaScoreRoundRange: undefined,
  avgQuackMessageScoreRoundRange: undefined,
  avgSentiment: undefined,
  resolutionStatus: undefined,
  escalationReason: undefined,
  skipReason: undefined,
  resolvedReason: undefined,
  tenantId: undefined,
  week: undefined,
  isQuackAnswered: undefined,
  isAnsweredByAui: undefined,
  originUrl: undefined,
  scoreTitle: undefined,
  scoreContent: undefined,
  simulator: undefined,
  skillUsed: undefined,
  avgCsat: undefined,
};

const groupByToColumn: Record<
  ExploreGroupByColumnType,
  ExploreColumnType | null
> = {
  tenantId: "tenantId",
  status: "status",
  channel: "channel",
  topic: "topic",
  category: "category",
  resolutionStatus: "resolutionStatus",
  escalationReason: "escalationReason",
  skipReason: "skipReason",
  resolvedReason: "resolvedReason",
  subTopic: "subTopic",
  agentAssigned: "agentAssigned",
  team: "team",
  week: null,
  day: null,
  month: null,
  avgQaScore: "avgQaScore",
  avgQaScoreRoundRange: "avgQaScoreRoundRange",
  avgQuackMessageScoreRoundRange: "avgQuackMessageScoreRoundRange",
  avgSentiment: "avgSentiment",
  isQuackAnswered: "isQuackAnswered",
  isAnsweredByAui: "isAnsweredByAui",
  scoreTitle: "scoreTitle",
  scoreContent: "scoreContent",
  originUrl: "originUrl",
  simulator: "simulatorId",
  skillUsed: "skillUsed",
  avgCsat: "avgCsat",
};

function getGroupByFilter(
  groupBy: string,
  groupValue?: string | number | boolean
): FieldFilter {
  if (groupBy === "isQuackAnswered" || groupBy === "isAnsweredByAui") {
    // Handle boolean field with eq operator
    if (groupValue == null) {
      return {
        operator: "isnull",
        value: "true",
      };
    }

    return {
      operator: "eq",
      value: Boolean(groupValue),
    };
  }

  if (groupValue == null) {
    // Handle other fields with in operator
    return {
      operator: "isnull",
      value: "true",
    };
  }

  return {
    operator: "in",
    value: [groupValue.toString()],
  };
}

export function useHandleGroupByClick({
  state,
  allowedGroupByOptions,
  notAllowedGroupByOptions,
}: {
  state: ExploreState;
  allowedGroupByOptions: ExploreGroupByColumnType[];
  notAllowedGroupByOptions: ExploreGroupByColumnType[];
}) {
  return useCallback(
    (row: ExploreAggInteractionRow): ExploreState | undefined => {
      const { groupBy } = state;

      if (groupBy == null) {
        return;
      }

      const groupValue = row[groupBy as keyof ExploreAggInteractionRow];
      const groupByFilterKey =
        groupByToColumn[groupBy as ExploreGroupByColumnType] ?? groupBy;
      const groupByFilterValue = getGroupByFilter(groupBy, groupValue);

      let nextGroupBy = isExploreGroupByColumnType(groupBy)
        ? hierarchy[groupBy]
        : undefined;

      if (nextGroupBy != null && !allowedGroupByOptions.includes(nextGroupBy)) {
        nextGroupBy = undefined;
      }

      if (
        nextGroupBy != null &&
        notAllowedGroupByOptions.includes(nextGroupBy)
      ) {
        nextGroupBy = undefined;
      }

      return {
        ...state,
        groupBy: nextGroupBy,
        sorting: [
          {
            id: nextGroupBy != null ? "volume" : "createdAt",
            desc: true,
          },
        ],
        filters: {
          ...state.filters,
          [groupByFilterKey]: groupByFilterValue,
        },
      };
    },
    [state, allowedGroupByOptions, notAllowedGroupByOptions]
  );
}
